/**
 * chain29 Restaurant OS - the dining screens (STEP 5).
 * Routes (mounted at /dining/* on the main app and on pos.<domain>):
 *   /dining                 -> the signed-in person's home screen (by role)
 *   /dining/floor           Floor & Tables (host, waiter, cashier, manager)
 *   /dining/table/:id       Order pad for a table session (waiter, manager)
 *   /dining/kds             Kitchen display (kitchen, manager; read for expo)
 *   /dining/expo            Expo / waiter board (expo, waiter, manager)
 *   /dining/checkout        Checkout - choose a table (cashier, waiter, manager)
 *   /dining/checkout/:id    Checkout for a table session
 *   /dining/checkout/closed Closed checks: receipts, refunds / voids after release
 *   /dining/chat            Guest chat inbox (chat.reply: every POS role)
 * Contract: docs/DINING_API_2026-10-28.md, docs/CHECKOUT_API_2026-10-28.md.
 * Design: docs/design_refs/restaurant_os/*.dc.html.
 */
import React, { useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './theme/dining.css';
import { hasToken } from './lib/api';
import { DiningSession, homeFor, screensFor, signInHref, signOutStaff, useMe, usePerms } from './lib/session';
import { C, FONT_TIGHT } from './theme/tokens';
import { Btn, Loading, Logo29, ToastProvider } from './components/ui';
import FloorScreen from './screens/FloorScreen';
import OrderPadScreen from './screens/OrderPadScreen';
import KDSScreen from './screens/KDSScreen';
import ExpoScreen from './screens/ExpoScreen';
import CheckoutScreen, { CheckoutHome } from './screens/CheckoutScreen';
import ChatScreen from './screens/ChatScreen';
import ClosedChecksScreen from './screens/ClosedChecksScreen';
import { useChatPresence } from './lib/chat';

function Gate({ title, body, action }) {
  return (
    <div className="c29 c29-dark" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: 460, maxWidth: '100%', background: C.panel, border: `1px solid ${C.line}`, borderRadius: 24, padding: 32, display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'flex-start' }}>
        <Logo29 size={52} />
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FDBA74' }}>chain29 Restaurant OS</span>
        <h1 style={{ margin: 0, fontFamily: FONT_TIGHT, fontSize: 30, fontWeight: 800, color: C.textHi, letterSpacing: '-0.5px' }}>{title}</h1>
        <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55, color: C.textMid }}>{body}</p>
        {action}
      </div>
    </div>
  );
}

function SignInGate({ reason }) {
  const location = useLocation();
  const next = `${location.pathname}${location.search}`;
  return (
    <Gate title="Sign in to start your shift"
      body={reason || 'The floor, kitchen, expo and checkout screens open with your staff PIN. Your role decides what you see.'}
      action={<Btn kind="hero" full onClick={() => window.location.assign(signInHref(next))}>Sign in with your PIN</Btn>} />
  );
}

function Guard({ me, need, children }) {
  const screens = screensFor(me);
  if (need && !screens.some((s) => s.key === need)) {
    const home = homeFor(me);
    if (home) return <Navigate to={`/dining/${home}`} replace />;
    return <Gate title="Nothing to show here" body="Your role has no Restaurant OS screen. Ask a manager to check your role in Back Office → Employees." />;
  }
  return children;
}

function Signed() {
  const { me, error, loading, reload } = useMe();
  const perms = usePerms(me);
  const ctx = useMemo(() => ({ me, perms, reload }), [me, perms, reload]);
  useChatPresence(me); // guests see the restaurant "online" while someone who answers chat is on a screen
  if (loading && !me) {
    return <div className="c29 c29-dark" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loading label="Signing in…" /></div>;
  }
  if (error && !me) {
    if (error.status === 401 || error.status === 403) {
      signOutStaff();
      return <SignInGate reason={error.status === 403 ? 'This sign-in is not a restaurant staff sign-in. Sign in with your staff PIN.' : 'Your sign-in has ended. Sign in again with your PIN.'} />;
    }
    return <Gate title="Cannot reach chain29" body={error.message} action={<Btn kind="hero" full onClick={reload}>Try again</Btn>} />;
  }
  const home = homeFor(me);
  return (
    <DiningSession.Provider value={ctx}>
      <Routes>
        <Route index element={home ? <Navigate to={home} replace /> : <Guard me={me} need="none" />} />
        <Route path="floor" element={<Guard me={me} need="floor"><FloorScreen /></Guard>} />
        <Route path="table/:sessionId" element={<OrderPadScreen />} />
        <Route path="kds" element={<Guard me={me} need="kds"><KDSScreen /></Guard>} />
        <Route path="expo" element={<Guard me={me} need="expo"><ExpoScreen /></Guard>} />
        <Route path="checkout" element={<Guard me={me} need="checkout"><CheckoutHome /></Guard>} />
        <Route path="checkout/closed" element={<Guard me={me} need="checkout"><ClosedChecksScreen /></Guard>} />
        <Route path="checkout/:sessionId" element={<Guard me={me} need="checkout"><CheckoutScreen /></Guard>} />
        <Route path="chat" element={<Guard me={me} need="chat"><ChatScreen /></Guard>} />
        <Route path="*" element={<Navigate to="/dining" replace />} />
      </Routes>
    </DiningSession.Provider>
  );
}

export default function DiningApp() {
  return (
    <ToastProvider>
      {hasToken() ? <Signed /> : <SignInGate />}
    </ToastProvider>
  );
}
